import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { useActiveTasks } from '@/hooks/useProjects'
import { ListTodo, Clock, Circle, Calendar, ArrowRight } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import { Link } from 'react-router-dom'

export function ActiveTasksWidget() {
  const { data: tasks, isLoading } = useActiveTasks()

  const todoTasks = tasks?.filter(t => t.status === 'todo') || []
  const inProgressTasks = tasks?.filter(t => t.status === 'in_progress') || []
  const allActive = [...inProgressTasks, ...todoTasks]

  if (isLoading) {
    return (
      <Card>
        <div className="p-6 flex justify-center">
          <Spinner />
        </div>
      </Card>
    )
  }

  if (allActive.length === 0) {
    return (
      <Card>
        <div className="p-6">
          <EmptyState
            icon={<ListTodo className="w-8 h-8 text-brown-300" />}
            title="Aucune tache active"
            description="Toutes les taches sont terminees"
          />
        </div>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ListTodo className="w-5 h-5" />
          Taches actives
        </CardTitle>
        <div className="flex items-center gap-2">
          {inProgressTasks.length > 0 && (
            <Badge variant="blue" size="sm">
              {inProgressTasks.length} en cours
            </Badge>
          )}
          {todoTasks.length > 0 && (
            <Badge variant="gray" size="sm">
              {todoTasks.length} a faire
            </Badge>
          )}
        </div>
      </CardHeader>

      <div className="divide-y divide-beige-200">
        {allActive.slice(0, 8).map(task => (
          <Link
            key={task.id}
            to={`/projects/${task.project_id}?tab=tasks`}
            className="block px-6 py-3 hover:bg-beige-50 transition-colors"
          >
            <div className="flex items-start gap-3">
              {task.status === 'in_progress' ? (
                <Clock className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              ) : (
                <Circle className="w-4 h-4 text-brown-400 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-brown-900 text-sm truncate">
                  {task.titre}
                </p>
                <div className="flex items-center gap-3 mt-1">
                  {task.project && (
                    <span className="text-xs text-brown-500 truncate flex items-center gap-1">
                      <ArrowRight className="w-3 h-3 flex-shrink-0" />
                      {task.project.title}
                    </span>
                  )}
                  {task.due_date && (
                    <span className="text-xs text-brown-400 flex items-center gap-1 flex-shrink-0">
                      <Calendar className="w-3 h-3" />
                      {formatDate(task.due_date, { day: 'numeric', month: 'short' })}
                    </span>
                  )}
                </div>
                {task.progress > 0 && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 bg-beige-200 rounded-full h-1">
                      <div
                        className="bg-mint-500 h-1 rounded-full"
                        style={{ width: `${task.progress}%` }}
                      />
                    </div>
                    <span className="text-xs text-brown-400">{task.progress}%</span>
                  </div>
                )}
              </div>
              <Badge
                variant={task.status === 'in_progress' ? 'blue' : 'gray'}
                size="sm"
                className="flex-shrink-0"
              >
                {task.status === 'in_progress' ? 'En cours' : 'A faire'}
              </Badge>
            </div>
          </Link>
        ))}
      </div>

      {allActive.length > 8 && (
        <div className="px-6 py-3 border-t border-beige-200 text-center">
          <Link
            to="/projects"
            className="text-sm text-mint-600 hover:text-mint-700 font-medium"
          >
            Voir toutes les taches ({allActive.length})
          </Link>
        </div>
      )}
    </Card>
  )
}
